import { ArrowRightOutline, modalStyles } from '@aslanpay/portal-common-lib';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, Resolver, useForm } from 'react-hook-form';
import usePots, { companyPotQueryParams } from 'app/hooks/usePots';
import useFetchCompany from 'app/hooks/useFetchCompany';
import CenterSpinner from 'app/components/UI/CenterSpinner';

import usePoints from 'app/hooks/usePoints';

import PotInformation from '../../TransferPointsModal/PotInformation/PotInformation';

import {
  TopUpPotFormFields,
  DEFAULT_CREATE_POT_FORM_DATA,
  TopUpPotFormProps,
  TransferPotInfo,
  createSchema,
  PREDEFINED_AMOUNTS,
} from './TopUpPotForm.types';

function TopUpPotForm({
  onClose,
  onSuccess,
  onError,
  companyId,
  originGroupWithPot,
}: TopUpPotFormProps) {
  // Fetch company data
  const { data: companyData, isLoading: isLoadingCompany } = useFetchCompany(companyId);

  // Fetch company points bag
  const { data: companyPots, isLoading: isLoadingCompanyPot } = usePots.useFetchPots(
    companyPotQueryParams(companyId)
  );

  const companyPot = companyPots?.list?.[0];
  const currentBalance = companyPot?.balance || 0;

  const schema = useMemo(() => createSchema(currentBalance), [currentBalance]);

  const methods = useForm<TopUpPotFormFields>({
    resolver: yupResolver(schema) as Resolver<TopUpPotFormFields>,
    mode: 'onChange',
    defaultValues: DEFAULT_CREATE_POT_FORM_DATA,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty },
  } = methods;

  const amount = watch('amount');

  useEffect(() => {
    if (amount) {
      trigger('amount');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, trigger]);

  const { mutate: transferPoints } = usePoints.useTransferPoints({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTopUpPot = (data: TopUpPotFormFields) => {
    const points = parseInt(data.amount || '0', 10);
    const potInfo: TransferPotInfo = {
      fromPotName: companyData?.name || 'Company Wallet',
      toPotName: originGroupWithPot.pot?.name || 'pot',
      points,
    };

    setIsSubmitting(true);
    transferPoints(
      {
        companyId,
        fromPotId: companyPot?.id || '',
        toPotId: originGroupWithPot?.pot?.id || '',
        points,
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          onSuccess?.(potInfo);
        },
        onError: () => {
          setIsSubmitting(false);
          onError?.(potInfo);
        },
      }
    );
  };

  const handleCancel = () => {
    onClose();
  };

  const handleConfirm = (data: TopUpPotFormFields) => {
    handleTopUpPot(data);
  };

  const renderPredefinedAmounts = useMemo(() => {
    return (
      <HStack w="full" gap="10px">
        {PREDEFINED_AMOUNTS.map((option) => {
          const resolvedValue = option.value === '__all__' ? String(currentBalance) : option.value;
          const isSelected = amount === resolvedValue;
          return (
            <Flex
              key={option.value}
              cursor="pointer"
              w="100px"
              h="40px"
              p="8px 20px"
              bg={isSelected ? 'brand.purple' : 'brand.lightGray2'}
              borderRadius="20px"
              border="1px solid"
              borderColor={isSelected ? 'brand.purple' : 'brand.lightGray1'}
              align="center"
              justify="center"
              color={isSelected ? 'brand.white' : 'brand.dark'}
              _hover={{ bg: 'brand.purple', color: 'brand.white', borderColor: 'brand.purple' }}
              onClick={() =>
                setValue('amount', resolvedValue, { shouldValidate: true, shouldDirty: true })
              }
            >
              {option.label}
            </Flex>
          );
        })}
      </HStack>
    );
  }, [amount, currentBalance, setValue]);

  if (isLoadingCompany || isLoadingCompanyPot) {
    return <CenterSpinner />;
  }

  return (
    <FormProvider {...methods}>
      <VStack spacing={6} align="stretch" maxW="100%" w="100%">
        {/* Pot Information */}
        <PotInformation
          name={originGroupWithPot.pot?.name || 'Current Pot'}
          currentBalance={originGroupWithPot.pot?.balance || 0}
        />

        <Flex gap="20px" alignItems="flex-start" flexDir="column">
          <FormControl isInvalid={!!errors?.amount} isRequired flex={1}>
            <FormLabel>No. of points</FormLabel>
            <Box position="relative">
              <Controller
                control={control}
                name="amount"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter amount"
                    isInvalid={!!errors.amount}
                    value={value ? parseInt(value, 10).toLocaleString() : ''}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, '');
                      if (raw === '' || /^\d+$/.test(raw)) {
                        onChange(raw || undefined);
                      }
                    }}
                    onBlur={onBlur}
                  />
                )}
              />
            </Box>
            <FormErrorMessage>{errors?.amount?.message as string}</FormErrorMessage>
          </FormControl>

          {renderPredefinedAmounts}
        </Flex>

        <Text color="system.error" textStyle="extraSmallMedium">
          * mandatory
        </Text>

        {/* Footer Buttons */}
        <HStack spacing={3} justify="flex-end" p={modalStyles.baseStyle.footer.p}>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            type="submit"
            colorScheme="brand"
            rightIcon={<ArrowRightOutline />}
            isLoading={isSubmitting}
            isDisabled={!isValid || !isDirty}
            onClick={() => {
              handleSubmit(handleConfirm)();
            }}
          >
            Confirm
          </Button>
        </HStack>
      </VStack>
    </FormProvider>
  );
}

export default TopUpPotForm;
